import React, { useState } from 'react';
import { Eye, Check, X, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSupabase } from '../../hooks/useSupabase';
import { aicisAPI } from '../../services/api';
import DataTable from '../../components/Tables/DataTable';
import { formatDate, getStatusColor, getStatusLabel } from '../../utils/helpers';

export default function DiplomaticSignIn() {
  const { data, refetch } = useSupabase(aicisAPI.getDiplomaticSignIns);
  const [processingId, setProcessingId] = useState(null);

  const handleStatusUpdate = async (id, status) => {
    console.log(`Updating status for ID ${id} to ${status}`);
    setProcessingId(id);
    try {
      await aicisAPI.updateDiplomaticSignInStatus(id, status);
      toast.success(`Sign in ${status} successfully`);
      refetch();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(`Failed to ${status} sign in`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (id) => {
    console.log(`Deleting ID ${id}`);
    if (window.confirm('Are you sure you want to delete this sign in?')) {
      setProcessingId(id);
      try {
        await aicisAPI.deleteDiplomaticSignIn(id);
        toast.success('Sign in deleted successfully');
        refetch();
      } catch (error) {
        console.error('Error deleting:', error);
        toast.error('Failed to delete sign in');
      } finally {
        setProcessingId(null);
      }
    }
  };

  const handleView = (item) => {
    console.log('Viewing item:', item);
    // Show a toast with the item details
    toast.custom((t) => (
      <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
        <div className="flex-1 w-0 p-4">
          <div className="flex items-start">
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900">
                {item.name || item.full_name || 'Unknown'}
              </p>
              <p className="text-sm text-gray-500">{item.email || 'No email'}</p>
              <p className="text-xs text-gray-400 mt-1">
                ID: {item.id_number || 'N/A'} | Phone: {item.phone || 'N/A'}
              </p>
              <p className="text-xs text-gray-400">
                Institution: {item.institution_name || 'N/A'}
              </p>
              <p className="text-xs text-gray-400">
                Status: {item.status || 'pending'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex border-l border-gray-200">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Close
          </button>
        </div>
      </div>
    ), {
      duration: 5000,
    });
  };

  const columns = [
    { key: 'id_number', label: 'ID Number', sortable: true },
    { 
      key: 'name', 
      label: 'Full Name', 
      sortable: true,
      render: (value) => value || '-'
    },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'institution_name', label: 'Institution' },
    { 
      key: 'status', 
      label: 'Status',
      render: (value) => (
        <span className={`badge ${getStatusColor(value)}`}>
          {getStatusLabel(value)}
        </span>
      )
    },
    { 
      key: 'created_at', 
      label: 'Signed In',
      render: (value) => formatDate(value)
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: <Eye className="w-4 h-4" />,
      onClick: (item) => handleView(item),
      className: 'text-blue-600 hover:bg-blue-50',
      showWhen: ['pending', 'approved', 'rejected']
    },
    {
      label: 'Approve',
      icon: <Check className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'approved'),
      className: 'text-green-600 hover:bg-green-50',
      showWhen: ['pending'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Reject',
      icon: <X className="w-4 h-4" />,
      onClick: (item) => handleStatusUpdate(item.id, 'rejected'),
      className: 'text-red-600 hover:bg-red-50',
      showWhen: ['pending'],
      disabled: (item) => processingId === item.id
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: (item) => handleDelete(item.id),
      className: 'text-gray-600 hover:bg-gray-50',
      showWhen: ['pending', 'approved', 'rejected'],
      disabled: (item) => processingId === item.id
    },
  ];

  console.log('DiplomaticSignIn data:', data);
  console.log('Actions:', actions);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Diplomatic Sign In</h1>
        <p className="text-gray-600">Manage diplomatic sign ins</p>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        title={`Total: ${data?.length || 0} sign ins`}
        actions={actions}
        searchPlaceholder="Search sign ins..."
        onRowClick={(item) => console.log('Row clicked:', item)}
      />
    </div>
  );
}